import { BOOTSTRAP_DIGITAL_DATA } from "../../consts";
import { ICartTotals } from "../interfaces/ICartTotals";
import { IDigitalData } from "../interfaces/IDigitalData";
import { DataLayer } from "../interfaces/digitalData/dataLayer";
import { ICartItem } from "./../interfaces/ICartItem";
import AnalyticsService from "./analyticsService";
import { ApiEndpointResolver } from "./apiEndpointResolver";
import EnvironmentService from "./environmentService";
import HttpClient from "./httpClient";

let _digitalData: IDigitalData | null = null;
let _userConfigId: string;

function getDigitalData(): IDigitalData | null {
  if (typeof window === "undefined") {
    return null;
  }

  if ((window as unknown as { digitalData: IDigitalData }).digitalData) {
    return (window as unknown as { digitalData: IDigitalData }).digitalData;
  }

  const ecomDigitalData = EnvironmentService.get().ecomDigitalData;

  if (ecomDigitalData) {
    return _digitalData
      ? _digitalData
      : (_digitalData = JSON.parse(ecomDigitalData as unknown as string));
  }

  return null;
}

export function setUserConfigId(value: string) {
  _userConfigId = value;
}

const DigitalDataService = {
  userConfigId: (): string => {
    if (_userConfigId) {
      return _userConfigId;
    }

    const digitalData = getDigitalData();

    return digitalData?.userInfo?.userConfigID || "";
  },
  setEvent: (event: { event: string }, skipEcomCheck?: boolean) => {
    if (!EnvironmentService.get().isEcom && !skipEcomCheck) {
      return;
    }

    const digitalData = getDigitalData();

    if (digitalData) {
      digitalData.eventInfo = Object.assign(
        digitalData.eventInfo ? digitalData.eventInfo : {},
        event
      );
    }
  },
  dispatchEventType(eventType: string): void {
    document.body.dispatchEvent(new CustomEvent(eventType));
  },
  setSearchType(searchType: string, searchTerm: string) {
    const digitalData = getDigitalData();
    if (digitalData && digitalData.searchInfo) {
      digitalData.searchInfo.searchType = searchType;
      digitalData.searchInfo.searchTerm = searchTerm;
    }
  },
  removeFromCart(cartTotals: ICartTotals, cartItems: ICartItem[]): void {
    const digitalData = getDigitalData();

    if (!digitalData) {
      return;
    }

    digitalData.eCommerce.productInfo = cartItems.map((ci) => {
      return {
        productSKU: ci.id,
        productName: ci.description,
      };
    });

    digitalData.eventInfo.event = "removeFromCart";
    AnalyticsService.satelliteTrack("removeFromCart");
  },
  // proceedToCheckout is already handled by cart slide out
  proceedToCheckout(cartTotals: ICartTotals) {
    if (EnvironmentService.get().isEcom) {
      return;
    }
    const digitalData = getDigitalData();

    if (!digitalData || !cartTotals || !cartTotals.items) {
      return;
    }

    digitalData.eCommerce.productInfo = cartTotals.items.map((ci) => {
      return {
        productSKU: ci.itemLineRecId,
        productName: ci.description,
      };
    });

    digitalData.eventInfo.event = "proceedToCheckout";
  },
  viewCart(cartTotals: ICartTotals) {
    if (EnvironmentService.get().isEcom) {
      return;
    }

    const digitalData = getDigitalData();

    if (!digitalData || !cartTotals || !cartTotals.items) {
      return;
    }

    digitalData.eCommerce.productInfo = cartTotals.items.map((ci) => {
      return {
        productSKU: ci.itemLineRecId,
        productName: ci.description,
      };
    });

    digitalData.eventInfo.event = "viewCart";
  },
  addNewList() {
    this.dispatchEventType("shoppingListCreated");
  },
  listViewed() {
    this.dispatchEventType("shoppingListViewed");
  },
  listItemAdded() {
    if (EnvironmentService.get().isEcom) {
      return;
    }

    AnalyticsService.satelliteTrack("productAdded");
  },
  listItemRemoved() {
    if (EnvironmentService.get().isEcom) {
      return;
    }

    AnalyticsService.satelliteTrack("productRemoved");
  },
  setCartId(cartId: string) {
    if (EnvironmentService.get().isEcom) {
      return;
    }

    const digitalData = getDigitalData();

    if (digitalData && digitalData.eCommerce) {
      digitalData.eCommerce.cartID = cartId;
    }
  },
  bootstrapDigitalData() {
    if (BOOTSTRAP_DIGITAL_DATA() && typeof window !== "undefined") {
      const windowObject = window as unknown as any;

      if (!windowObject.digitalData) {
        HttpClient.get<unknown>({
          baseUri: ApiEndpointResolver.getDigitalDataUrl(),
        }).subscribe((partialDataLayer) => {
          if (!windowObject.digitalData) {
            const dummyDataLayer: DataLayer = {
              accountInfo: {
                employeesCount: "",
                organizationIndustry: "",
                secasTokens: "",
                sensitiveFlag: false,
              },
              eCommerce: {
                cartID: "",
                productImpression: [],
                productInfo: [],
              },
              configPropInfo: {},
              pageInfo: {
                pageName: "",
                pageType: "",
                siteSection: "",
                pageNavigationHierarchy: "n/a",
                pageTitle: "",
                server: "",
              },
              searchInfo: {
                searchTerm: "",
                numberOfSearchTerms: 0,
                resultsPerPage: "",
                linkPosition: 0,
                searchType: "",
                autoCorrectTerm: "",
                facetSelected: "",
                sortMethod: "",
                pageLayout: "",
                typeSelected: "",
                filterSelected: "",
                tableItemPosition: 0,
              },
              userInfo: {
                configID: "",
                userConfigID: "",
                loggedInStatus: "",
                userZipCode: "",
                visitPurpose: "",
                accountNumber: "",
                groupConfigID: "",
                siteConfigID: "",
                punchOutFlag: "",
                restrictionLevel: "",
                branch: "",
                priceCustomizationLevel: "",
                marketingFlag: "",
                salesRepID: "",
                contactPersonID: "",
                internalUser: "",
                searchEngineId: "",
                configLevel: 0,
                inheritProdSetsLevel: 0,
                customerAccountName: "",
                firstName: "",
                customerStartDate: "",
                autoCouponList: "",
              },
              eventInfo: {
                event: "",
                eventLabel: "",
                wasFirstProductAddedToCart: false,
              },
            };

            const digitalDataObject = Object.assign(
              dummyDataLayer,
              partialDataLayer
            );

            windowObject.digitalData = digitalDataObject;
          }
        });
      }
    }
  },
};

export default DigitalDataService;
